import { useState, useLayoutEffect, useEffect, } from 'react';
import { Link, useParams, } from 'react-router-dom';
import { useFirstMountState, } from 'react-use';
import ReactCardFlip from 'react-card-flip';
import { useSnackbar, } from 'notistack';
import {
    Alert,
    Box,
    Button,
    Typography,
    IconButton,
    Stack,
    Grid,
    Tooltip,
    MobileStepper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ButtonGroup,
    Chip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SchoolIcon from '@mui/icons-material/School';
import LanguageIcon from '@mui/icons-material/Language';
import { useAuth, useDocument, useFirestore, useMathcesDown, } from '../hooks';
import SimpleBackdrop from '../components/simple-backdrop';
import { unescapeHTML, } from '../utils';

const LearningCardsPage = () => {
    const [progress, setProgress] = useState(null);
    const [filteredTerms, setFilteredTerms] = useState([]);
    const [filteredByFav, setFilteredByFav] = useState(false);
    const [isFlippedCard, setIsFlippedCard] = useState(false);
    const [openDialogEndViewing, setOpenDialogEndViewing] = useState(false);
    const [openDialogParameters, setOpenDialogParameters] = useState(false);
    const { id, } = useParams();
    const { user, } = useAuth();
    const { updateDocument, } = useFirestore('sets');

    const { document, isPending, error, } = useDocument({
        col: 'sets',
        id,
        uid: user.uid,
    });

    const isFirstMount = useFirstMountState();
    const matchesMd = useMathcesDown('md');
    const { enqueueSnackbar, } = useSnackbar();

    const terms = document?.terms;
    const isPositiveTermsFav = document?.terms.some((term) => term.fav);
    const answer = document?.modes.learningCards.answer;
    const termsType = document?.modes.learningCards.termsType;
    const progressServer = document?.modes.learningCards.progress;

    const styles = {
        cardStyles: {
            bgcolor: 'common.white',
            border: 1,
            borderColor: 'borderColor.light',
            borderRadius: [4, 5, 6, 7],
            boxShadow: 1,
            cursor: 'pointer',
            my: 2.5,
            px: [4, 6, 8, 10],
            py: [8, 11, 11, 15],
            textAlign: 'center',
            minWidth: ['80%', '65%', '65%', '55%'],
            maxWidth: ['100%', '90%', '90%', '85%'],
        },
        cardTextStyles: {
            color: 'common.black',
            fontSize: [26, 30, 34, 38],
            fontWeight: 500,
            lineHeight: 1.32,
            overflowWrap: 'anywhere',
        },
        buttonAdditionalSectionStyles: {
            border: 1,
            borderColor: 'common.pattensBlue',
            color: 'common.pattensBlue',
            fontSize: [13, 14],
            py: 1,
            px: 2,
        },
        iconButtonArrowStyles: {
            border: 1,
            borderColor: 'borderColor.main',
            p: [0.5, 0.7, 0.9, 1.1],
            '&:disabled': {
                opacity: 0.35,
            },
        },
        buttonDialogParametersStyles(propName, value) {
            return {
                bgcolor: document.modes.learningCards[propName] === value ? 'common.kashmirBlue' : 'common.white',
                color: document.modes.learningCards[propName] === value ? 'common.white' : 'common.black',
            };
        },
    };

    const filterTerms = (terms) => {
        if (!filteredByFav) {
            return terms;
        }

        let filteredTerms;

        if (filteredByFav) {
            filteredTerms = terms.filter((term) => term.fav);
        }

        return filteredTerms;
    };

    const handleOpenDialogEndViewing = () => {
        setOpenDialogEndViewing(true);
    };

    const handleCloseDialogEndViewing = () => {
        setOpenDialogEndViewing(false);
    };

    const handleOpenDialogParameters = () => {
        setOpenDialogParameters(true);
    };

    const handleCloseDialogParameters = () => {
        setOpenDialogParameters(false);
    };

    const handleRestartCurrentCard = async () => {
        await updateDocument(id, { 'modes.learningCards.progress': 0, });
        setIsFlippedCard(false);
    };

    const handleToggleFlipCard = () => {
        setIsFlippedCard((v) => !v);
    };

    const handleTermFav = (documentId, terms, index) => {
        let updatedTerms;

        const updateTerms = (terms, updatedTerm) => {
            const copyTerms = [...terms];
            copyTerms[index] = updatedTerm;
            updatedTerms = copyTerms;
        };

        if (terms[index]?.fav) {
            const copyWithoutFav = {
                ...terms[index],
            };

            delete copyWithoutFav.fav;

            updateTerms(terms, copyWithoutFav);
        } else {
            const termWithFav = {
                ...terms[index],
                fav: true,
            };

            updateTerms(terms, termWithFav);
        }

        updateDocument(documentId, { terms: updatedTerms, });
    };

    const incrementCurrentCard = () => {
        if (progress === (filteredTerms.length - 1)) {
            handleOpenDialogEndViewing();
            return;
        }

        if (progress <= filteredTerms.length - 2) {
            setIsFlippedCard(false);
            updateDocument(id, { 'modes.learningCards.progress': progress + 1, });
        }
    };

    const decrementCurrentCard = () => {
        setIsFlippedCard(false);
        updateDocument(id, { 'modes.learningCards.progress': progress - 1, });
    };

    useLayoutEffect(() => {
        document && setProgress(progressServer);
    }, [progressServer]);

    useLayoutEffect(() => {
        document && setFilteredByFav(termsType === 'fav' ? true : false);
    }, [termsType]);

    useLayoutEffect(() => {
        terms && setFilteredTerms(filterTerms(terms));
    }, [terms, filteredByFav]);

    useLayoutEffect(() => {
        if (termsType === 'fav' && !isPositiveTermsFav) {
            updateDocument(id, { 'modes.learningCards.termsType': 'all', });
        }
    }, [isPositiveTermsFav]);

    return (
        isFirstMount
            ? null
            : isPending
                ? <SimpleBackdrop open={isPending} />
                : error
                    ? <Alert severity='error' variant='filled'>{error.message}</Alert>
                    : document
                        ? <>
                            <Typography
                                component='h2'
                                sx={{
                                    fontSize: [18, 21, 24, 27],
                                    fontWeight: 700,
                                    mb: document.description ? undefined : [4, 5, 6, 7],
                                    overflowWrap: 'anywhere',
                                }}>
                                {document.title}
                            </Typography>

                            {
                                document.description && <Typography sx={{
                                    color: 'text.secondary',
                                    fontSize: [11, 12, 13],
                                    fontStyle: 'italic',
                                    overflowWrap: 'anywhere',
                                    mb: [4, 5, 6, 7],
                                }}>
                                    {document.description}
                                </Typography>
                            }

                            <Grid
                                container
                                direction='row'
                                sx={{
                                    border: 1,
                                    borderColor: 'borderColor.main',
                                    boxShadow: 2,
                                }}>
                                <Grid item xs={12} md={9} lg={10}>
                                    <Stack direction='column' sx={{ bgcolor: 'common.white', p: 3.5, }}>
                                        <Stack
                                            direction='row'
                                            justifyContent='space-between'
                                            alignItems='center'
                                            sx={{
                                                mb: 1.5,
                                            }}>
                                            <Stack
                                                direction={{
                                                    xs: 'column',
                                                    sm: 'row',
                                                }}
                                                spacing={1}
                                                sx={{
                                                    '& .MuiChip-root': {
                                                        borderColor: 'grey.400',
                                                        borderRadius: 1,
                                                        color: 'grey.400',
                                                        '& .MuiSvgIcon-root': {
                                                            color: 'grey.400',
                                                        },
                                                    },
                                                }}>
                                                <Tooltip title='Текущий режим' placement='top' arrow>
                                                    <Chip
                                                        icon={<SchoolIcon />}
                                                        label='Карточки'
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                                <Tooltip title='Вариант ответа' placement='top' arrow>
                                                    <Chip
                                                        icon={<LanguageIcon />}
                                                        label={answer === 'rus' ? 'Русский' : 'Английский'}
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                                <Tooltip title='Изучаемые термины' placement='top' arrow>
                                                    <Chip
                                                        icon={<StarIcon />}
                                                        label={termsType === 'all' ? 'Все' : 'Избранные'}
                                                        variant='outlined'
                                                        size='small' />
                                                </Tooltip>
                                            </Stack>
                                            <Stack direction='row' justifyContent='flex-start' spacing={1.5}>
                                                <Tooltip title={(filteredTerms[progress]?.fav) ? 'Удалить карточку из избранного' : 'Добавить карточку в избранное'} placement='top' arrow>
                                                    <IconButton
                                                        onClick={() => {
                                                            if (termsType === 'fav') {
                                                                const idx = terms.findIndex((term) => filteredTerms[progress].id === term.id);
                                                                handleTermFav(document.id, terms, idx);

                                                                if (filteredTerms.length === 1 && progress === 0) {
                                                                    setFilteredByFav(false);
                                                                    updateDocument(id, { 'modes.learningCards.termsType': 'all', });
                                                                    enqueueSnackbar('Режим изучения терминов переведён на «Все»', { variant: 'info', });
                                                                } else if (progress > 0) {
                                                                    setProgress((progress) => progress - 1);
                                                                    updateDocument(id, { 'modes.learningCards.progress': progress - 1, });
                                                                }
                                                            } else if (termsType === 'all') {
                                                                handleTermFav(document.id, terms, progress);
                                                            }
                                                        }}
                                                        sx={{ p: 0, }}>
                                                        <StarIcon sx={{
                                                            color: filteredTerms[progress]?.fav ? 'common.gold' : 'grey.400',
                                                            fontSize: [22, 24, 25, 27],
                                                            '&:hover': {
                                                                color: 'common.gold',
                                                            },
                                                        }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title='Перевернуть карточку' placement='top' arrow>
                                                    <IconButton
                                                        onClick={handleToggleFlipCard}
                                                        sx={{ p: 0, }}>
                                                        <AutorenewIcon sx={{ color: 'common.kashmirBlue', fontSize: [22, 24, 25, 27], }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={progress === 0 ? 'Невозможно начать сначала' : 'Начать сначала'} placement='top' arrow>
                                                    <Box component='span'>
                                                        <IconButton
                                                            disabled={progress === 0}
                                                            onClick={handleRestartCurrentCard}
                                                            sx={{ p: 0, }}>
                                                            <RestartAltIcon
                                                                sx={{
                                                                    color: (progress === 0)
                                                                        ? 'grey.400'
                                                                        : 'common.kashmirBlue',
                                                                    fontSize: [22, 24, 25, 27],
                                                                }} />
                                                        </IconButton>
                                                    </Box>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                        <Box sx={{ color: 'common.black', fontSize: '14px', fontWeight: 'bold', mt: 5, textAlign: 'center', }}>Кликните по карточке, чтобы посмотреть перевод</Box>
                                        <ReactCardFlip
                                            isFlipped={isFlippedCard}
                                            flipDirection='vertical'>
                                            <Stack direction='row' justifyContent='center'>
                                                <Box onClick={handleToggleFlipCard} sx={{ ...styles.cardStyles, }}>
                                                    <Typography sx={{ ...styles.cardTextStyles, }}>{unescapeHTML(answer === 'rus' ? filteredTerms[progress].eng : filteredTerms[progress].rus)}</Typography>
                                                </Box>
                                            </Stack>

                                            <Stack direction='row' justifyContent='center'>
                                                <Box onClick={handleToggleFlipCard} sx={{ ...styles.cardStyles, }}>
                                                    <Typography sx={{ ...styles.cardTextStyles, }}>{unescapeHTML(answer === 'rus' ? filteredTerms[progress].rus : filteredTerms[progress].eng)}</Typography>
                                                </Box>
                                            </Stack>
                                        </ReactCardFlip>
                                        <Box sx={{ alignSelf: 'center', }}>
                                            <IconButton
                                                disabled={progress === 0}
                                                onClick={decrementCurrentCard}
                                                sx={{ ...styles.iconButtonArrowStyles, mr: [0.85, 0.95, 1.1], }}>
                                                <ArrowBackIcon sx={{ color: 'common.black', fontSize: [19, 20, 22, 24], }} />
                                            </IconButton>
                                            <IconButton
                                                onClick={incrementCurrentCard}
                                                sx={{ ...styles.iconButtonArrowStyles, ml: [0.85, 0.95, 1.1], }}>
                                                <ArrowForwardIcon sx={{ color: 'common.black', fontSize: [19, 20, 22, 24], }} />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={3} lg={2}>
                                    <Stack
                                        direction='column'
                                        justifyContent='space-around'
                                        sx={{
                                            bgcolor: 'common.kashmirBlue',
                                            height: {
                                                md: '100%',
                                            },
                                            minHeight: {
                                                xs: 250,
                                                md: 'auto',
                                            },
                                            p: 3.5,
                                        }}>
                                        <Stack direction='column' spacing={1.5}>
                                            <Button
                                                startIcon={matchesMd ? <ArrowBackIosIcon /> : undefined}
                                                endIcon={!matchesMd ? <ArrowForwardIosIcon /> : undefined}
                                                component={Link}
                                                to={`/set/${id}`}
                                                sx={{
                                                    ...styles.buttonAdditionalSectionStyles,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: 12,
                                                    },
                                                }}>К сету</Button>
                                            <Button
                                                startIcon={<TuneIcon />}
                                                onClick={handleOpenDialogParameters}
                                                sx={{
                                                    ...styles.buttonAdditionalSectionStyles,
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: 15,
                                                    },
                                                }}>Параметры</Button>
                                        </Stack>
                                        <Stack
                                            direction='column'
                                            alignItems='center'
                                            sx={{
                                                mt: [-0.5, -0.5, 0],
                                            }}>
                                            <Typography
                                                sx={{
                                                    color: 'common.pattensBlue',
                                                    fontSize: 13,
                                                    mb: 0.75,
                                                }}>Прогресс {progress + 1}/{filteredTerms.length}</Typography>
                                            <MobileStepper
                                                variant='progress'
                                                steps={filteredTerms.length}
                                                position='static'
                                                activeStep={progress}
                                                sx={{
                                                    alignSelf: 'stretch',
                                                    bgcolor: 'transparent',
                                                    justifyContent: 'center',
                                                    p: 0,
                                                    '& .MuiMobileStepper-progress': {
                                                        bgcolor: 'common.white',
                                                        height: 7,
                                                        width: '100%',
                                                    },
                                                    '& .MuiLinearProgress-bar': {
                                                        bgcolor: 'secondary.main',
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                            <Dialog
                                open={openDialogEndViewing}
                                aria-labelledby='alert-dialog-title-end-viewing'
                                aria-describedby='alert-dialog-description-end-viewing'
                            >
                                <DialogTitle
                                    id='alert-dialog-title-end-viewing'
                                    sx={{
                                        bgcolor: 'common.kashmirBlue',
                                        color: 'common.pattensBlue',
                                        display: 'block',
                                    }}>
                                    Карточки
                                </DialogTitle>
                                <DialogContent>
                                    <DialogContentText id='alert-dialog-description-end-viewing'>
                                        Для продолжения работы выберите один из предложенных вариантов
                                    </DialogContentText>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        alignItems: ['stretch', 'center'],
                                        flexDirection: ['column', 'row'],
                                    }}>
                                    <Button
                                        onClick={() => {
                                            handleRestartCurrentCard();
                                            handleCloseDialogEndViewing();
                                        }}
                                        sx={{
                                            mr: [0, 1],
                                            mb: [1, 0],
                                        }}>Начать сначала</Button>
                                    <Button
                                        onClick={handleCloseDialogEndViewing}
                                        component={Link}
                                        to={`/set/${id}`}
                                    >
                                        Перейти к сету
                                    </Button>
                                </DialogActions>
                            </Dialog>
                            <Dialog
                                open={openDialogParameters}
                                aria-labelledby='alert-dialog-title-parameters'>
                                <DialogTitle
                                    id='alert-dialog-title-parameters'
                                    sx={{
                                        bgcolor: 'common.kashmirBlue',
                                        color: 'common.pattensBlue',
                                        display: 'block',
                                    }}>
                                    Параметры
                                </DialogTitle>
                                <DialogContent>
                                    <Box sx={{
                                        mt: 3,
                                        p: [1, 1.5, 2.5, 3.5],
                                        '& .MuiTypography-root': {
                                            color: 'common.black',
                                            fontSize: 14,
                                            fontWeight: 700,
                                            mb: 1,
                                        },
                                        '& .MuiButtonGroup-root': {
                                            width: '100%',
                                        },
                                        '& .MuiButton-root': {
                                            border: 1,
                                            borderColor: 'common.kashmirBlue',
                                            width: '100%',
                                            '&:hover': {
                                                borderColor: 'common.kashmirBlue',
                                                bgcolor: 'common.kashmirBlue',
                                                color: 'common.white',
                                            },
                                            '&:disabled': {
                                                bgcolor: 'grey.100',
                                                borderColor: 'grey.400',
                                                color: 'grey.400',
                                            },
                                        },
                                    }}>
                                        <Stack direction='column' spacing={4.5}>
                                            <Box>
                                                <Typography>Вариант ответа:</Typography>
                                                <ButtonGroup orientation='vertical'>
                                                    <Button
                                                        onClick={() => {
                                                            if (answer === 'eng') {
                                                                updateDocument(id, { 'modes.learningCards.answer': 'rus', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('answer', 'rus'),
                                                            cursor: (answer === 'rus') ? 'default' : 'pointer',
                                                        }}>Русский</Button>
                                                    <Button
                                                        onClick={() => {
                                                            if (answer === 'rus') {
                                                                updateDocument(id, { 'modes.learningCards.answer': 'eng', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('answer', 'eng'),
                                                            cursor: (answer === 'eng') ? 'default' : 'pointer',
                                                        }}>Английский</Button>
                                                </ButtonGroup>
                                            </Box>
                                            <Box>
                                                <Typography>Изучаемые термины:</Typography>
                                                <ButtonGroup orientation='vertical'>
                                                    <Button
                                                        onClick={() => {
                                                            setFilteredByFav(false);
                                                            setProgress(0);

                                                            if (termsType === 'fav') {
                                                                updateDocument(id, { 'modes.learningCards.progress': 0, });
                                                                updateDocument(id, { 'modes.learningCards.termsType': 'all', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('termsType', 'all'),
                                                            cursor: (termsType === 'all') ? 'default' : 'pointer',
                                                        }}>Все</Button>
                                                    <Button
                                                        disabled={!isPositiveTermsFav}
                                                        onClick={() => {
                                                            setFilteredByFav(true);
                                                            setProgress(0);

                                                            if (termsType === 'all') {
                                                                updateDocument(id, { 'modes.learningCards.progress': 0, });
                                                                updateDocument(id, { 'modes.learningCards.termsType': 'fav', });
                                                            }
                                                        }}
                                                        sx={{
                                                            ...styles.buttonDialogParametersStyles('termsType', 'fav'),
                                                            cursor: (termsType === 'fav') ? 'default' : 'pointer',
                                                        }}>Только избранные</Button>
                                                </ButtonGroup>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        alignItems: ['stretch', 'center'],
                                        flexDirection: ['column', 'row'],
                                    }}>
                                    <Button
                                        onClick={handleCloseDialogParameters}
                                        sx={{
                                            mr: [0, 1],
                                            mb: [1, 0],
                                        }}>Закрыть</Button>
                                </DialogActions>
                            </Dialog>
                        </>
                        : <Typography sx={{ fontSize: 14, }}>Запрашиваемые данные отсутствуют</Typography>
    );
};

export default LearningCardsPage;